import { Input, Modal } from 'antd';
import { messages } from '../../../../../messages';
import { ApiErrorDetails } from '../../../../apiErrorDetails/apiErrorDetails';
import { LoadingIndicator } from '../../../../loadingIndicator/loadingIndicator';
import { Form } from '../../../../form/form';
import style from '../styling/widgetRowStyling.module.scss';
import { CustomerWidgetCategorySelect } from '../categories/customerWidgetCategorySelect';
import React, { useState } from 'react';
import { useI18n } from '../../../../../hooks/useI18n';
import { useNotificationStore } from '../../../../../hooks/useNotificationStore/notificationStore';
import { useFormik } from 'formik';
import { OrderType, TimePeriodType } from '../../../../../types';
import { ApiError, useApiRequests } from '../../../../../hooks/useApi';
import { ArticleCategoryPicker } from '../../../../pickers/articleCategoryPicker';
import { categoryCodesApi } from '../../../../../api/categoryCodesApi';
import { OrderTypePicker } from '../../../../pickers/orderTypePicker';
import { DeliveryStatusPicker } from '../../../../pickers/deliveryStatusPicker';
import { TimePeriodPicker } from '../../../../pickers/timePeriodPicker';
import { TwitterPicker } from 'react-color';
import { defaultWidgetColors } from '../../../../internal/widgets/widgetColors';
import {
  CreateUpdateCustomerOrdersReportWidgetRequest,
  customerCustomerOrdersReportWidgetApi,
} from '../../../../../api/widgets/customers/customerCustomerOrdersReportWidgetApi';

export const CreateCustomerOrdersReportWidgetModal = ({
  onClose,
}: {
  onClose: () => void;
}) => {
  const { t } = useI18n();
  const { notify } = useNotificationStore();

  const [name, setName] = useState<string>('');
  const [widgetCategoryId, setWidgetCategoryId] = useState<number | undefined>(
    undefined,
  );
  const [color, setColor] = useState<string | undefined>(undefined);
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [orderType, setOrderType] = useState<OrderType>(OrderType.Standard);
  const [deliveryStatus, setDeliveryStatus] = useState<number>(0);
  const [timePeriods, setTimePeriods] = useState<{
    type: TimePeriodType;
    periods: string[];
  }>({
    type: TimePeriodType.Yearly,
    periods: [],
  });

  const [error, setError] = useState<ApiError>();

  const createWidget = useApiRequests(
    customerCustomerOrdersReportWidgetApi.createWidget,
  );

  const handleFormSubmit = () => {
    const createWidgetData: CreateUpdateCustomerOrdersReportWidgetRequest = {
      name: name,
      widgetCategoryId: widgetCategoryId,
      color: color,
      categoryIds: categoryIds,
      orderType: orderType,
      deliveryStatus: deliveryStatus,
      timePeriods: timePeriods.periods,
      timePeriodType: timePeriods.type,
    };

    createWidget
      .create({
        onSuccess: () => {
          notify({
            title: t(messages.widgets.widgetCreated),
            type: 'success',
          });
          onClose();
        },
        onError: setError,
      })
      .send(createWidgetData);
  };

  const formik = useFormik<FormData>({
    initialValues: {
      name: '',
      categoryIds: [],
    },
    onSubmit: handleFormSubmit,
  });

  function handleWidgetCategoryOnChange(widgetCategoryId: string | undefined) {
    const id =
      widgetCategoryId === undefined
        ? widgetCategoryId
        : parseInt(widgetCategoryId);
    setWidgetCategoryId(id);
  }

  function handleSelectedCategoryIds(selectedCategoryIds: number[]) {
    setCategoryIds(selectedCategoryIds.slice(0, 300));
  }

  const selectableOrderTypes: OrderType[] = [
    OrderType.Standard,
    OrderType.Sample,
    OrderType.Forecast,
  ];

  return (
    <Modal
      title={t(messages.widgets.customerOrdersReport.create)}
      visible={true}
      closable={false}
      onCancel={onClose}
      onOk={formik.submitForm}
      okText={t(messages.shared.save)}
      okButtonProps={{ disabled: createWidget.isLoading }}
      width={720}
    >
      {createWidget.isLoading && <LoadingIndicator />}
      {error && <ApiErrorDetails error={error} />}
      <Form form={formik}>
        <div className={style.inputRow}>
          <h4>{t(messages.shared.name)}</h4>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t(messages.widgets.widgetNamePlaceholder)}
          />
          <CustomerWidgetCategorySelect
            value={widgetCategoryId?.toString()}
            onChange={handleWidgetCategoryOnChange}
          />
        </div>
        <div className={style.inputRow}>
          <h4>{t(messages.shared.color)}</h4>
          <TwitterPicker
            width="100%"
            triangle="hide"
            colors={defaultWidgetColors}
            color={color}
            onChange={(color) => setColor(color.hex)}
          />
        </div>
        <div className={style.inputRow}>
          <h4>{t(messages.reports.articleCategory)}</h4>
          <ArticleCategoryPicker
            categoryIds={categoryIds}
            onChange={handleSelectedCategoryIds}
            categoryRequestFactory={(_) => categoryCodesApi.getCategories}
          />
        </div>
        <div className={style.inputRow}>
          <h4>{t(messages.order.orderType)}</h4>
          <OrderTypePicker
            orderType={orderType}
            selectableOrderTypes={selectableOrderTypes}
            onChange={setOrderType}
            allowClear={false}
          />
        </div>
        {orderType !== OrderType.Forecast && (
          <div className={style.inputRow}>
            <h4>{t(messages.order.progress)}</h4>
            <DeliveryStatusPicker
              deliveryStatus={deliveryStatus}
              onChange={setDeliveryStatus}
            />
          </div>
        )}
        <div className={style.inputRow}>
          <h4>{t(messages.reports.timePeriod)}</h4>
          <TimePeriodPicker onChange={setTimePeriods} />
        </div>
      </Form>
    </Modal>
  );
};

interface FormData {
  name: string;
  categoryIds: number[];
}
